import type { DbTarget, SettingsPatch } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { getConfigLayer, getConfigVersion, putConfigLayer } from '../api/config.js';
import { flattenSettings } from './settings-fields.js';
import { SettingsForm } from './SettingsForm.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * One server's own settings layer (knowledge doc section 11).
 *
 * The last layer, so anything set here wins. What makes this the useful place to
 * edit it is the "inherited" column: the values shown beside the unticked fields
 * are this server's **current document** rather than the schema's defaults, so a
 * field says what this machine is actually doing before anybody overrides it.
 */

export function ServerSettingsCard({
  serverId,
  latestVersion,
  detectedDatabases,
}: {
  serverId: string;
  /** Null before this server has ever been given a version. */
  latestVersion: number | null;
  /** What this server's installer prepared to monitor, from its heartbeat. */
  detectedDatabases?: readonly DbTarget[] | undefined;
}): ReactNode {
  const queryClient = useQueryClient();
  const scopeRef = { scope: 'server', id: serverId } as const;

  const layer = useQuery({
    queryKey: ['config-layer', 'server', serverId],
    queryFn: () => getConfigLayer(scopeRef),
  });

  /** What this server is running now, which is what "inherited" means here. */
  const current = useQuery({
    queryKey: ['config-version', serverId, latestVersion],
    queryFn: () => getConfigVersion(serverId, latestVersion ?? 0),
    enabled: latestVersion !== null,
  });

  const [draft, setDraft] = useState<SettingsPatch>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (layer.data !== undefined) setDraft(layer.data.layer?.settings ?? {});
  }, [layer.data]);

  const save = useMutation({
    mutationFn: () => putConfigLayer(scopeRef, draft),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['config-layer'] });
      void queryClient.invalidateQueries({ queryKey: ['config-versions', serverId] });
      void queryClient.invalidateQueries({ queryKey: ['server', serverId] });
    },
    onError: (caught: unknown) => {
      setSaved(false);
      setError(caught instanceof Error ? caught.message : 'Could not save those settings.');
    },
  });

  if (layer.isPending) {
    return (
      <Card title="Settings for this server">
        <Loading />
      </Card>
    );
  }

  if (layer.isError) {
    return (
      <Card title="Settings for this server">
        <Alert tone="danger" title="Could not load this server’s settings">
          Try again in a moment.
        </Alert>
      </Card>
    );
  }

  const inherited =
    current.data === undefined
      ? undefined
      : flattenSettings({
          allowlist: current.data.document.allowlist,
          modules: current.data.document.modules,
        });

  return (
    <Card
      title="Settings for this server"
      description="Overrides that apply to this machine alone. Anything not overridden comes from its group, or from the global defaults."
      aside={<Link to="/settings">Edit those instead</Link>}
    >
      <SettingsForm
        settings={draft}
        onChange={setDraft}
        {...(inherited === undefined ? {} : { inherited })}
        detectedDatabases={detectedDatabases}
        disabled={save.isPending}
      />

      {error !== null && <Alert tone="danger">{error}</Alert>}
      {saved && (
        <Alert tone="success" title="Saved">
          This server picks it up within seconds.
        </Alert>
      )}

      <div className="row">
        <Button
          variant="primary"
          busy={save.isPending}
          busyLabel="Saving…"
          onClick={() => {
            setSaved(false);
            save.mutate();
          }}
        >
          Save settings
        </Button>
      </div>
    </Card>
  );
}
